import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../../hooks.ts';
import { getSubscription } from '../../store/selectors/subscription.selector.ts';

interface UserSettingsProps {
  fullName: string;
  email: string;
  profilePictureUrl: string;
  onNameChange: (name: string) => void;
  onProfilePictureChange: (displayUrl: string, base64String?: string) => void;
  getInitials: (name: string) => string;
}

export const AccountSettings: React.FC<UserSettingsProps> = ({
  fullName,
  email,
  profilePictureUrl,
  onNameChange,
  onProfilePictureChange,
  getInitials,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subscription = useAppSelector(getSubscription);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Create a displayable URL for the UI
      const displayUrl = URL.createObjectURL(file);

      // Also convert to base64 for backend storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64DataUrl = e.target?.result as string;
        const base64String = base64DataUrl.split(',')[1]; // Extract just the base64 part

        // Pass both the display URL and the base64 string
        onProfilePictureChange(displayUrl, base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onNameChange(e.target.value);
  };

  const userInfo = [
    { label: t('settings.account.fullName'), value: fullName, editable: true },
    { label: t('settings.account.email'), value: email, editable: false },
    { label: t('settings.account.role'), value: t('common.role'), editable: false },
    {
      label: t('settings.account.subscription'),
      value: subscription?.tier
        ? t(`settings.plans.${subscription.tier.toLowerCase()}`)
        : t('settings.account.freeTier'),
      editable: false,
    },
    {
      label: t('settings.account.companyName'),
      value: t('common.company'),
      editable: false,
    },
    {
      label: t('settings.account.address'),
      value: t('common.address'),
      editable: false,
    },
  ];

  return (
    <div className="flex flex-col lg:flex-row min-h-[486px] border-b border-greyP5 p-4 md:p-8">
      <div className="w-full lg:w-[280px] lg:pr-8 h-auto mb-8 lg:mb-0">
        <div className="flex flex-col justify-between items-center h-full">
          <div className="relative group">
            <div className="w-32 h-32 md:w-48 md:h-48 flex items-center justify-center rounded-full overflow-hidden mb-6 bg-gray-800">
              {profilePictureUrl ? (
                <img src={profilePictureUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-white">{getInitials(fullName)}</span>
              )}
            </div>
            <button
              type="button"
              onClick={handleImageClick}
              className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              <span className="text-sm text-white">{t('common.change')}</span>
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[70%] lg:pl-8">
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:gap-6">
            {userInfo.map((info, index) => (
              <div key={index} className="flex flex-col sm:flex-row sm:items-center py-2 md:py-3 gap-1 sm:gap-4">
                <span className="text-greyP2 text-xs md:text-sm font-medium w-full sm:w-[140px]">{info.label}</span>
                {info.editable && (
                  <input
                    type="text"
                    value={fullName}
                    onChange={handleNameChange}
                    placeholder={!fullName ? t('settings.account.enterFullName') : ''}
                    className={`
                    w-full sm:w-[200px]
                    bg-transparent
                    text-white
                    text-sm
                    font-medium
                    outline-none
                    border-b
                    border-transparent
                    focus:border-green01
                    transition-colors`}
                  />
                )}

                {!info.editable && (
                  <span className="text-white text-sm font-medium w-full sm:w-[200px] truncate">{info.value}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
